import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { GitMerge } from 'lucide-react';
import { api, type Me, type TicketState } from '../lib/api.js';
import { hasCapability } from '../lib/access.js';
import { ago } from '../lib/format.js';
import { Panel, StateBadge, Tooltip } from '../ui/index.js';
import { ManageDialog } from './TicketManage.js';
import { FoldTitle, usePanelFold } from './PanelFold.js';

interface HistoryTicket {
  number: number;
  subject: string;
  state: TicketState;
  requestType: string | null;
  createdAt: string;
  resolvedAt: string | null;
}

interface History {
  tickets: HistoryTicket[];
  open: HistoryTicket[];
  repeat: { requestType: string; count: number; days: number } | null;
}

/** Shown before "Show all": enough to see a pattern, short enough to stay out of the way. */
const FIRST = 5;

/**
 * "What this person asked lately": their other tickets here, for the agent.
 *
 * CONTEXT, NOT CONTROLS, so it sits under the ticket's own settings and
 * starts folded to one line -- which still says the two things that change
 * what the agent does: a repeat, and their other open tickets.
 */
export function RequesterHistory({ tenant, number, me }: { tenant: string; number: number; me: Me | undefined }) {
  const q = useQuery({
    queryKey: ['requester-history', tenant, number],
    queryFn: () => api.get<History>(`/api/v1/tenants/${tenant}/tickets/${number}/requester-history`),
    enabled: hasCapability(me, tenant, 'ticket.read.queue'),
    staleTime: 60_000,
  });
  const [open, toggle] = usePanelFold('requester-history');
  const [all, setAll] = useState(false);
  const [mergeInto, setMergeInto] = useState<number | null>(null);
  const canMerge = hasCapability(me, tenant, 'ticket.merge');

  if (!q.data || q.data.tickets.length === 0) return null;
  const { tickets, open: stillOpen, repeat } = q.data;

  const summary = (
    <>
      {repeat && (
        <span className="text-warn">
          {ordinal(repeat.count)} {repeat.requestType} in {repeat.days} days ·{' '}
        </span>
      )}
      {stillOpen.length > 0 && <span className="text-info">{stillOpen.length} open · </span>}
      <span className="text-ink-tertiary">{tickets.length} in 90 days</span>
    </>
  );

  const openNumbers = new Set(stillOpen.map((t) => t.number));
  // Open ones first, so a merge is never behind "Show all".
  const ordered = [...tickets.filter((t) => openNumbers.has(t.number)), ...tickets.filter((t) => !openNumbers.has(t.number))];
  const shown = all ? ordered : ordered.slice(0, FIRST);

  return (
    <Panel title={<FoldTitle open={open} onToggle={toggle} title="What this person asked lately" summary={summary} />}>
      {open && (
        <div className="space-y-1.5">
          {repeat && (
            <p className="text-2xs text-warn">
              {ordinal(repeat.count)} {repeat.requestType} in {repeat.days} days. The last fix may not be holding.
            </p>
          )}
          <ul className="divide-y divide-line-subtle">
            {shown.map((t) => (
              <li key={t.number} className="flex items-center gap-2 py-1.5 text-2xs">
                <Link to={`/t/${tenant}/${t.number}`} className="nums shrink-0 font-medium text-brand hover:underline">
                  #{t.number}
                </Link>
                <span className="min-w-0 flex-1 truncate text-ink-secondary" title={t.subject}>
                  {t.subject}
                </span>
                {/* Open ones get the merge, in the row: one line each, not a card. */}
                {openNumbers.has(t.number) && canMerge && (
                  <Tooltip content={`Merge this ticket into #${t.number}`}>
                    <button
                      type="button"
                      onClick={() => setMergeInto(t.number)}
                      aria-label={`Merge this ticket into #${t.number}`}
                      className="shrink-0 rounded p-0.5 text-ink-tertiary hover:bg-surface-hover hover:text-brand"
                    >
                      <GitMerge aria-hidden className="h-3 w-3" />
                    </button>
                  </Tooltip>
                )}
                <StateBadge state={t.state} />
                <span className="shrink-0 text-ink-tertiary">{ago(t.createdAt)}</span>
              </li>
            ))}
          </ul>
          {tickets.length > FIRST && (
            <button type="button" onClick={() => setAll((v) => !v)} className="text-2xs text-brand hover:underline">
              {all ? 'Show fewer' : `Show all ${tickets.length}`}
            </button>
          )}
        </div>
      )}

      {mergeInto !== null && (
        <ManageDialog
          kind="merge"
          tenant={tenant}
          number={number}
          me={me}
          into={mergeInto}
          onClose={() => setMergeInto(null)}
        />
      )}
    </Panel>
  );
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}
